import { ArrowRight } from 'lucide-react';
import { T, fmtMoney, mix } from '../theme';
import type { OptimizationVM, SsPersonVM } from '../../formatters/deckContent';

interface Props {
  vm: OptimizationVM;
}

/** Framing 1 — isolate the delta (Social Security): per-person Before → After columns. */
export function DeltaPanel({ vm }: Props) {
  const ss = vm.ss;
  if (!ss) return null;

  const beforeTotal = ss.before.reduce((s, p) => s + p.benefit, 0);
  const afterTotal = ss.after.reduce((s, p) => s + p.benefit, 0);

  const changedAfter = ss.after.find((p) => p.changed);
  const changedBefore = changedAfter ? ss.before.find((p) => p.name === changedAfter.name) : undefined;
  const pct =
    changedAfter && changedBefore && changedBefore.benefit > 0
      ? Math.round(((changedAfter.benefit - changedBefore.benefit) / changedBefore.benefit) * 100)
      : null;
  const unchangedNames = ss.after.filter((p) => !p.changed).map((p) => p.name);

  const headline = changedAfter
    ? unchangedNames.length > 0
      ? `${changedAfter.name} waits, ${unchangedNames.join(' & ')} stays, combined benefit rises`
      : `${changedAfter.name} waits, combined benefit rises`
    : 'Optimized claiming raises the combined benefit';
  const subline = changedAfter
    ? `Only ${changedAfter.name}'s claim age changes.${
        unchangedNames.length > 0 ? ` ${unchangedNames.join(' & ')} keeps claiming as they are now.` : ''
      }`
    : 'Claim timing is optimized across the household.';

  const Column = ({ title, people, total, after }: { title: string; people: SsPersonVM[]; total: number; after: boolean }) => (
    <div
      style={{
        flex: 1,
        background: after ? T.growthSoft : '#F4F6F8',
        borderRadius: 12,
        padding: '16px 16px',
        border: `1px solid ${after ? T.growth : T.grayLine}`,
      }}
    >
      {/* state label */}
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: after ? T.growth : T.gray,
          marginBottom: 10,
          textAlign: 'center',
        }}
      >
        {title}
      </div>

      {/* Combined benefit — the dominant element, on top */}
      <div
        style={{
          textAlign: 'center',
          paddingBottom: 12,
          marginBottom: 12,
          borderBottom: `1px solid ${after ? mix(T.growth, 40) : T.grayLine}`,
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: 'uppercase',
            color: after ? T.growth : T.gray,
            marginBottom: 4,
          }}
        >
          Combined benefit
        </div>
        <div style={{ fontSize: 32, fontWeight: 700, color: after ? T.growth : T.navy, lineHeight: 1 }}>
          ${(total / 1000).toFixed(1)}K<span style={{ fontSize: 13, color: T.gray, fontWeight: 400 }}>/yr</span>
        </div>
      </div>

      {/* per-person detail, beneath */}
      {people.map((p) => (
        <div key={p.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.navy }}>
              {p.name}
              {p.changed && after && (
                <span style={{ fontSize: 10, fontWeight: 700, color: T.growth, marginLeft: 6 }}>▲ delayed</span>
              )}
            </div>
            <div style={{ fontSize: 11.5, color: T.gray }}>claims at {p.claimAge}</div>
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: T.gray }}>
            ${(p.benefit / 1000).toFixed(1)}K<span style={{ fontSize: 10, fontWeight: 400 }}>/yr</span>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="deck-chart-block">
      <div style={{ fontWeight: 600, color: T.navy, fontSize: 16, marginBottom: 2 }}>{headline}</div>
      <div style={{ fontSize: 13, color: T.gray, marginBottom: 20 }}>{subline}</div>

      {/* per-person before -> after */}
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 12, marginBottom: 20 }}>
        <Column title="Before" people={ss.before} total={beforeTotal} after={false} />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            minWidth: 64,
          }}
        >
          <ArrowRight size={26} color="var(--color-growth)" />
          {pct !== null && changedAfter && (
            <div style={{ fontSize: 11, color: T.growth, fontWeight: 700, textAlign: 'center' }}>
              +{pct}%
              <br />
              {changedAfter.name}
            </div>
          )}
        </div>
        <Column title="After" people={ss.after} total={afterTotal} after={true} />
      </div>

      {/* tie to lifetime total */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, background: T.navy, borderRadius: 12, padding: '18px 22px' }}>
        <div style={{ fontSize: 38, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{fmtMoney(vm.valueAdded)}</div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,.78)', lineHeight: 1.45 }}>
          {changedAfter ? `${changedAfter.name}'s higher yearly benefit` : 'The higher yearly benefit'}, compounded
          across the full plan, adds <strong style={{ color: '#fff' }}>{fmtMoney(vm.valueAdded)}</strong> to your
          plan's total value.
        </div>
      </div>
      <div style={{ fontSize: 12, color: T.gray, marginTop: 12, fontStyle: 'italic' }}>
        No tax cost, no reallocation, just a timing decision with guaranteed upside.
      </div>
    </div>
  );
}
